import {apiClient} from 'core/apiClient';
import {
    ApiFilters,
    ApiPaging,
    ApiSortings,
    SimplePaginationContent,
    SimplePaginationContentRaw,
} from 'types/api';

import {
    VirtualWarehouseGridAccessors,
    VirtualWarehouseGridRawRow,
    VirtualWarehouseGridRawRowResponse,
} from '../types';

const VIRTUAL_WAREHOUSE_URL = '/service/product-store'; // TODO wait for server

let gridTokensCache: Record<number, string | null> = {
    0: null,
};

export function clearWarehouseGridCache() {
    gridTokensCache = {
        0: null,
    };
}

const modifyParams = (params: {
    filters: ApiFilters;
    paging: ApiPaging;
    sortings: ApiSortings;
    gtinCollapsed?: boolean;
}) => {
    const participant = params.filters.find(
        (filter) => filter.id === VirtualWarehouseGridAccessors.participant,
    );
    if (participant?.value?.length) {
        const [value] = participant.value;
        // @ts-ignore
        params.participant = value;
    }

    const productName = params.filters.find(
        (filter) => filter.id === VirtualWarehouseGridAccessors.name,
    );
    if (productName?.value?.length) {
        const [value] = productName.value;
        // @ts-ignore
        params.productName = value;
    }

    const gtin = params.filters.find((filter) => filter.id === VirtualWarehouseGridAccessors.gtin);
    if (gtin?.value?.length) {
        const [value] = gtin.value;
        // @ts-ignore
        params.gtin = value;
    }

    const sortingGtinDesc = params.sortings.find(
        (filter) => filter.id === VirtualWarehouseGridAccessors.gtin,
    );
    if (sortingGtinDesc?.desc !== undefined) {
        // @ts-ignore
        params.sortingGtinDesc = sortingGtinDesc.desc;
    }

    const sortingProductNameDesc = params.sortings.find(
        (filter) => filter.id === VirtualWarehouseGridAccessors.name,
    );
    if (sortingProductNameDesc?.desc !== undefined) {
        // @ts-ignore
        params.sortingProductNameDesc = sortingProductNameDesc.desc;
    }

    const productGroupCode = params.filters.find(
        (filter) => filter.id === VirtualWarehouseGridAccessors.groupCode,
    );
    if (productGroupCode?.value?.length) {
        const [value] = productGroupCode.value;
        // @ts-ignore
        params.productGroupCode = value;
    }

    const productTrademark = params.filters.find(
        (filter) => filter.id === VirtualWarehouseGridAccessors.trademark,
    );
    if (productTrademark?.value?.length) {
        const [value] = productTrademark.value;
        // @ts-ignore
        params.productTrademark = value;
    }

    const markingCode = params.filters.find(
        (filter) => filter.id === VirtualWarehouseGridAccessors.code,
    );
    if (markingCode?.value?.length) {
        const [value] = markingCode.value;
        // @ts-ignore
        params.markingCode = value;
    }

    const storageName = params.filters.find(
        (filter) => filter.id === VirtualWarehouseGridAccessors.storageName,
    );
    if (storageName?.value?.length) {
        const [value] = storageName.value;
        // @ts-ignore
        params.storageLocationName = value;
    }

    const storageAddress = params.filters.find(
        (filter) => filter.id === VirtualWarehouseGridAccessors.storageAddress,
    );
    if (storageAddress?.value?.length) {
        const [value] = storageAddress.value;
        // @ts-ignore
        params.storageLocationAddress = value;
    }

    const packagingType = params.filters.find(
        (filter) => filter.id === VirtualWarehouseGridAccessors.type,
    );
    if (packagingType?.value?.length) {
        const [value] = packagingType.value;
        // @ts-ignore
        params.packagingType = value;
    }

    // @ts-ignore
    params.collapseByGtin = params?.gtinCollapsed || false;

    delete params.gtinCollapsed;
    params.filters = [];
    // params.sortings = [];
};

export async function fetchVirtualWarehouseGrid(params: {
    sortings: ApiSortings;
    filters: {
        [key: string]: string;
    }[];
    paging: ApiPaging;
}): Promise<SimplePaginationContent<VirtualWarehouseGridRawRow>> {
    const {pageSize, pageNumber} = params.paging;

    const mappedPaging = {pageSize, pageNumber: pageNumber + 1};
    const responseData: SimplePaginationContentRaw<VirtualWarehouseGridRawRow> & {
        page: number;
        size: number;
        totalElements: number;
        totalPages: number;
    } = await apiClient
        .post(VIRTUAL_WAREHOUSE_URL, {...params, paging: mappedPaging})
        .then((res) => res.data);

    return {
        content: responseData.data,
        hasNext: responseData.page < responseData.totalPages,
    };
}

export async function fetchVirtualWarehouseGridUngrouped(params: {
    sortings: ApiSortings;
    filters: ApiFilters;
    paging: ApiPaging;
    pageRef?: string | null;
}): Promise<SimplePaginationContent<VirtualWarehouseGridRawRow> & {totalCount: number}> {
    const {pageRef, ...restParams} = params;
    modifyParams(restParams);

    const {pageSize, pageNumber} = restParams.paging;
    const mappedPaging = pageNumber === 0 ? {pageSize} : {pageSize, pageRef};
    const responseData: SimplePaginationContentRaw<VirtualWarehouseGridRawRowResponse> & {
        totalCount: number;
        lastPage: boolean;
    } = await apiClient
        .post(VIRTUAL_WAREHOUSE_URL + '/withMC', {...restParams, paging: mappedPaging})
        .then((res) => res.data);

    return {
        content: responseData.data
            .map((el) =>
                el.markingCodes.map((markingCodes) => ({
                    ...el,
                    code: markingCodes.code,
                    type: markingCodes.packagingType,
                })),
            )
            .flat(),
        hasNext: !!responseData.nextPageRef,
        nextPageRef: responseData.nextPageRef,
        totalCount: responseData.totalCount,
        brands: responseData.brands,
        groups: responseData.groups,
    };
}
